import * as Sentry from '@sentry/vue';
import {
  HttpClient as HttpClientIntegration,
  ReportingObserver as ReportingObserverIntegration,
} from '@sentry/integrations';
import type { Router } from 'vue-router';
import { withScope } from '@sentry/vue';
import { defineNuxtPlugin } from '#app';

export default defineNuxtPlugin(async nuxtApp => {
  const { sentryDsn, sentryEnabled: _sentryEnabled }: { sentryDsn?: string; sentryEnabled?: string } = useRuntimeConfig().public;
  const sentryEnabled = _sentryEnabled?.toString().toLowerCase() === 'true';
  const DEBUG = isDevelop();

  if (sentryEnabled) {
    if (!sentryDsn && DEBUG) {
      console.log('Error: cannot find Sentry DSN, make sure the NUXT_PUBLIC_SENTRY_DSN env variable is set');
    }
    else if (DEBUG) {
      console.log('Enabling Sentry...');
    }

    Sentry.init({
      app: nuxtApp.vueApp,
      dsn: sentryDsn,
      environment: DEBUG ? 'development' : 'production',
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(nuxtApp.$router as Router, {
            routeLabel: 'path',
          }),
        }),
        new Sentry.Replay(),
        new HttpClientIntegration(),
        new ReportingObserverIntegration(),
      ],
      trackComponents: true,
      hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.5,

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
    });

    nuxtApp.vueApp.config.errorHandler = (err, context) => {
      withScope(scope => {
        scope.setExtra('context', context);
        Sentry.captureException(err);
        throw err;
      });
    };

    nuxtApp.hook('app:error', err => {
      Sentry.captureException(err);
    });
  }

  return {
    provide: {
      sentrySetContext: Sentry.setContext,
      sentrySetUser: Sentry.setUser,
      sentrySetTag: Sentry.setTag,
      sentryAddBreadcrumb: Sentry.addBreadcrumb,
      sentryCaptureException: Sentry.captureException,
    },
  };
});
